import { emitAuthDiagnostic } from './authDiagnostics';
import { getE2eAccessToken } from './sessionStorage';

const STALE_CLERK_ORGANISATION_RESET_KEY = 'flowqa-stale-clerk-organisation-reset-at';
const E2E_ACCESS_TOKEN_KEY = 'flowqa-e2e-access-token';

const flowqaStoragePrefixes = [
  'flowqa-auth',
  'flowqa-organisation-filter',
  'flowqa-project-filter',
  'flowqa-bootstrap-snapshot-cache',
  'flowqa-org-options-cache',
  'flowqa-project-options-cache',
  'flowqa-auth-bootstrap',
  'flowqa-session-expired',
  'flowqa-manual-signout',
  'flowqa-signout-transition-at',
];

const shouldRemoveFlowqaStorageKey = (key: string): boolean => {
  if (key === E2E_ACCESS_TOKEN_KEY) {
    return false;
  }
  return flowqaStoragePrefixes.some((prefix) => key === prefix || key.startsWith(`${prefix}:`));
};

export const hasAttemptedStaleClerkOrganisationRecovery = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.sessionStorage.getItem(STALE_CLERK_ORGANISATION_RESET_KEY) !== null;
};

export const clearFlowqaBrowserSessionState = (): void => {
  if (typeof window === 'undefined') {
    return;
  }
  Object.keys(window.localStorage).forEach((key) => {
    if (shouldRemoveFlowqaStorageKey(key)) {
      window.localStorage.removeItem(key);
    }
  });
  Object.keys(window.sessionStorage).forEach((key) => {
    if (shouldRemoveFlowqaStorageKey(key)) {
      window.sessionStorage.removeItem(key);
    }
  });
};

export const attemptStaleClerkOrganisationRecovery = async (input: {
  clerkSignOut: (options?: { redirectUrl?: string }) => Promise<void>;
  redirect?: (path: string) => void;
  setActive?: (params: { organization: string | null }) => Promise<unknown>;
  userId?: string | null;
}): Promise<boolean> => {
  if (typeof window === 'undefined') {
    return false;
  }
  if (getE2eAccessToken()) {
    return false;
  }
  if (hasAttemptedStaleClerkOrganisationRecovery()) {
    emitAuthDiagnostic('stale_clerk_organisation_recovery_skipped', {
      reason: 'already_attempted',
      userId: input.userId ?? null,
    });
    return false;
  }

  clearFlowqaBrowserSessionState();
  window.sessionStorage.setItem(STALE_CLERK_ORGANISATION_RESET_KEY, String(Date.now()));

  try {
    if (input.setActive) {
      await input.setActive({ organization: null });
    }
    emitAuthDiagnostic('stale_clerk_organisation_recovery_completed', {
      userId: input.userId ?? null,
      mode: input.setActive ? 'clear_active_organisation' : 'local_state_only',
    });
    const redirect =
      input.redirect ??
      ((path: string): void => {
        window.location.assign(path);
      });
    redirect('/sign-in');
    return true;
  } catch (error) {
    emitAuthDiagnostic('stale_clerk_organisation_recovery_fallback_signout', {
      userId: input.userId ?? null,
      error: error instanceof Error ? error.message : String(error),
    });
    await input.clerkSignOut({ redirectUrl: '/sign-in' });
    return true;
  }
};
